import type { Provenance } from './admissions'

/** Signals a user can individually withdraw from personalization. */
export type SignalKey =
  | 'search_history'
  | 'saved_items'
  | 'declared_preferences'
  | 'course_enrolment'
  | 'marketplace_activity'

export interface SignalDefinition {
  key: SignalKey
  label: string
  description: string
}

export const SIGNALS: SignalDefinition[] = [
  {
    key: 'search_history',
    label: 'Search history',
    description: 'What you have searched for across housing, programmes and the marketplace.',
  },
  {
    key: 'saved_items',
    label: 'Saved items',
    description: 'Listings, programmes, scholarships and roles you saved or shortlisted.',
  },
  {
    key: 'declared_preferences',
    label: 'Declared preferences',
    description: 'Budget, move-in date, roommate preferences and career interests you entered yourself.',
  },
  {
    key: 'course_enrolment',
    label: 'Course enrolment',
    description: 'The courses you are registered for this semester.',
  },
  {
    key: 'marketplace_activity',
    label: 'Marketplace activity',
    description: 'Orders, entitlements and providers you have worked with.',
  },
]

export type SignalState = Record<SignalKey, boolean>

/** A ranked suggestion that always carries the reason it appeared. */
export interface Recommendation<T> {
  item: T
  reason: string
  score: number
  /** True when personalization signals were withdrawn and this fell back to popularity. */
  fallback: boolean
}

export type AssistantSurface =
  | 'home'
  | 'discover'
  | 'academics'
  | 'housing'
  | 'community'
  | 'opportunities'
  | 'marketplace'
  | 'money'
  | 'support'
  | 'profile'
  | 'notes'
  | 'services'
  | 'social'
  /* Operator consoles. Each is named because "an operator" is not a role: a
     property manager and an institution registrar hold entirely different data
     and must never be answered from the same source. */
  | 'property'
  | 'institution'
  | 'provider'
  | 'author'
  | 'employer'
  | 'merchant'
  | 'representative'
  | 'admin'
  | 'operator'

export type AssistantCardKind =
  | 'listing'
  | 'application'
  | 'requirement'
  | 'provider'
  | 'resource'
  | 'order'
  | 'course'
  | 'role'
  | 'group'
  | 'balance'
  | 'queue'
  | 'metric'
  | 'section'

export interface AssistantCard {
  id: string
  kind: AssistantCardKind
  title: string
  meta: string
  detail?: string
  to?: string
  badge?: { label: string; tone: 'ok' | 'warn' | 'brand' | 'neutral' | 'danger' }
  provenance: Provenance
}

/** A consequential action the Assistant composed but will not perform itself. */
export interface AssistantConfirmation {
  id: string
  action: string
  summary: string
  lines: { label: string; value: string }[]
  /** True when the capability is still behind licensing or provider readiness. */
  gated: boolean
  gateNote?: string
  confirmLabel: string
}

export interface AssistantRefusal {
  reason: string
  whereToLook: string
}

export interface AssistantAnswer {
  text: string
  cards: AssistantCard[]
  confirmation?: AssistantConfirmation
  refusal?: AssistantRefusal
  /** What kind of information the answer itself is. */
  provenance: Provenance
}

export interface AssistantExchange {
  id: string
  question: string
  answer: AssistantAnswer
  surface: AssistantSurface
}
